/** Institutional gold palette — solid fills on neutral chart chrome. */
export const CHART_GOLD_RAMP = [
  '#8B6427',
  '#9D7A33',
  '#A67C1A',
  '#C49A43',
  '#C99A2E',
  '#D9B768',
  '#E5B840',
  '#F5C451',
  '#F6DF9A',
] as const

export const CHART_SERIES_COLORS = CHART_GOLD_RAMP

/**
 * Categorical pie / mix cohorts — intentionally multi-hue so adjacent slices
 * stay distinguishable on dark chrome. Gold leads for brand; remaining tones
 * are institutional (steel, copper, sage, sand, slate) rather than a gold ramp.
 */
export const CHART_PIE_COLORS = [
  '#C49A43', // refined gold
  '#5B8FA8', // steel blue
  '#C17F59', // copper
  '#7A8F6E', // muted sage
  '#B8A07A', // warm sand
  '#6B7C93', // slate
  '#D4B56A', // champagne
  '#8B6B4F', // bronze
] as const

export const CHART_PRIMARY_GOLD = '#C49A43' as const

export const CHART_GRID_STROKE = 'rgba(255, 255, 255, 0.06)'

export const CHART_AXIS_LINE_STROKE = 'rgba(255, 255, 255, 0.08)'

export const CHART_AXIS_TICK = { fill: '#a2a2a2', fontSize: 10 } as const

export const CHART_TOOLTIP_STYLE = {
  background: '#121212',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 8,
  fontSize: 12,
  color: '#e4e4e4',
} as const

export const CHART_TOOLTIP_LABEL_STYLE = {
  color: '#fafafa',
  fontWeight: 600,
} as const

export const CHART_TOOLTIP_ITEM_STYLE = {
  color: '#d4d4d4',
} as const

export const CHART_LEGEND_STYLE = {
  fontSize: 10,
  color: '#a2a2a2',
} as const

export const CHART_BAR_FILL_OPACITY = 1

export const CHART_AREA_FILL_OPACITY = 1

export const CHART_TOOLTIP_PROPS = {
  contentStyle: CHART_TOOLTIP_STYLE,
  labelStyle: CHART_TOOLTIP_LABEL_STYLE,
  itemStyle: CHART_TOOLTIP_ITEM_STYLE,
} as const

export function chartGridProps(horizontal = true) {
  return {
    strokeDasharray: '3 3',
    stroke: CHART_GRID_STROKE,
    horizontal,
  } as const
}

export function chartAxisProps(fontSize = 10) {
  return {
    tick: { fontSize, fill: CHART_AXIS_TICK.fill },
    axisLine: { stroke: CHART_AXIS_LINE_STROKE },
    tickLine: { stroke: CHART_AXIS_LINE_STROKE },
  } as const
}

export function chartAxisPropsMinimal(fontSize = 10) {
  return {
    tick: { fontSize, fill: CHART_AXIS_TICK.fill },
    tickLine: false as const,
    axisLine: false as const,
  } as const
}

/** Subject property vs peer benchmark bars. */
export function chartBenchmarkBarColor(isSubject: boolean, peerIndex = 1): string {
  return isSubject ? chartSeriesColor(0) : chartSeriesColor(peerIndex)
}

export function chartSeriesColor(index: number): string {
  return CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]
}

export function chartPieColor(index: number): string {
  return CHART_PIE_COLORS[index % CHART_PIE_COLORS.length]
}

/** Solid gold shades for stacked/part-of-whole bar segments. */
export function chartPartOfWholeColor(index: number, total?: number): string {
  const ramp = CHART_GOLD_RAMP
  if (total != null && total > 1) {
    const step = (ramp.length - 1) / (total - 1)
    const rampIndex = Math.min(ramp.length - 1, Math.round(index * step))
    return ramp[rampIndex]
  }
  return ramp[index % ramp.length]
}

/** Muted gold for inactive / historical chart segments (solid, no opacity wash). */
export function chartMutedGold(index: number, total?: number): string {
  const ramp = CHART_GOLD_RAMP
  if (total != null && total > 1) {
    const step = (ramp.length - 1) / (total - 1)
    const rampIndex = Math.max(0, Math.round(index * step) - 2)
    return ramp[rampIndex]
  }
  return ramp[Math.max(0, index % ramp.length - 2)]
}

export function chartSeriesFill(index: number): string {
  return chartSeriesColor(index)
}

/** Semantic bar colors for time / SLA style charts. */
export function chartSlaBarColor(value: number, thresholds: { warn: number; bad: number }): string {
  if (value > thresholds.bad) return CHART_GOLD_RAMP[0]
  if (value > thresholds.warn) return CHART_GOLD_RAMP[3]
  return CHART_GOLD_RAMP[7]
}

/** Track background for CSS progress / distribution bars. */
export const CHART_VIZ_TRACK = 'rgba(255, 255, 255, 0.04)' as const

/** Portfolio vs pipeline dual-bar colors (solid gold shades). */
export const CHART_PORTFOLIO_COLOR = CHART_GOLD_RAMP[3]
export const CHART_PIPELINE_COLOR = CHART_GOLD_RAMP[0]

/** Gold radial overlay for map / dossier panels. */
export const CHART_MAP_RADIAL_OVERLAY =
  'radial-gradient(circle at top right, rgba(217, 183, 104, 0.12), transparent 28%), linear-gradient(135deg, rgba(12, 11, 9, 0.88), rgba(6, 6, 5, 0.98))' as const

export const CHART_MAP_GRID_OVERLAY =
  'linear-gradient(rgba(217, 183, 104, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(217, 183, 104, 0.06) 1px, transparent 1px)' as const

/** Solid fill for horizontal CSS bars (funnels, progress, distribution). */
export function chartBarGradient(index: number, _endOffset = 1): string {
  return chartSeriesColor(index)
}

/** Solid fill per funnel stage. */
export function chartFunnelBarGradient(stageIndex: number, totalStages: number): string {
  return chartPartOfWholeColor(stageIndex, totalStages)
}

/** Solid fill for vertical mini-bars (sparklines, histograms). */
export function chartVerticalBarGradient(index: number): string {
  return chartSeriesColor(index)
}

/** Sparkline column — recent vs older bars use lighter/darker gold, full opacity. */
export function chartSparklineBarStyle(
  barIndex: number,
  totalBars: number,
  highlightRecentCount = 4,
): { background: string; opacity: number } {
  const isRecent = barIndex >= totalBars - highlightRecentCount
  return {
    background: isRecent ? chartSeriesColor(7) : chartSeriesColor(2),
    opacity: 1,
  }
}

/** Solid progress / completion bar fill. */
export function chartProgressGradient(index = 0): string {
  return chartSeriesColor(index)
}

/** Threshold-based progress fill (checklists, draw schedules). */
export function chartSemanticProgressColor(
  pct: number,
  thresholds: { warn: number; good: number } = { warn: 40, good: 80 },
): string {
  if (pct >= thresholds.good) return CHART_GOLD_RAMP[7]
  if (pct >= thresholds.warn) return CHART_GOLD_RAMP[3]
  return CHART_GOLD_RAMP[0]
}

/** Conversion-rate bar color. */
export function chartConversionRateColor(rate: number): string {
  if (rate >= 75) return CHART_GOLD_RAMP[7]
  if (rate >= 60) return CHART_GOLD_RAMP[3]
  return CHART_GOLD_RAMP[0]
}

/** Stacked capital-structure segment. */
export function chartStackSegmentColor(index: number, isEquity = false): string {
  if (isEquity) return '#535353'
  return chartSeriesColor(index)
}

/** Risk tier bar color. */
export function chartRiskTierColor(tier: 'low' | 'medium' | 'high'): string {
  switch (tier) {
    case 'low':
      return CHART_GOLD_RAMP[7]
    case 'medium':
      return CHART_GOLD_RAMP[3]
    case 'high':
      return CHART_GOLD_RAMP[0]
    default: {
      const _exhaustive: never = tier
      return _exhaustive
    }
  }
}

/** Progress board tone → gold shade. */
export function chartProgressToneColor(tone: 'green' | 'red' | 'amber'): string {
  switch (tone) {
    case 'green':
      return CHART_GOLD_RAMP[7]
    case 'red':
      return CHART_GOLD_RAMP[0]
    case 'amber':
      return CHART_GOLD_RAMP[3]
    default: {
      const _exhaustive: never = tone
      return _exhaustive
    }
  }
}

/** Pass / fail semantic bars — lighter gold pass, darker gold fail. */
export function chartPassFailColor(passed: boolean): string {
  return passed ? CHART_GOLD_RAMP[7] : CHART_GOLD_RAMP[0]
}

/** Alert / breach emphasis — darkest gold. */
export function chartAlertColor(): string {
  return CHART_GOLD_RAMP[0]
}
